import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Pencil, Luggage, FileText, Plane, BedDouble, Ticket, Utensils, ShoppingCart, Trash2, Calendar, Wallet, Shirt, Copy, MapPin, Icon, type LucideIcon } from '../ui/Icon';
import type { AiOperation } from '../../api/ai.api';
import Spinner from '../ui/Spinner';
import type { Pending } from './ProposalCards';

// Review cards for the proposals added with ADR-0027 phase 2 (edits, packing
// items, notes, tickets, meals, shopping). One shell, so every card accepts and
// discards the same way; each kind only decides what it shows.

export type TripPending = Extract<Pending, {
  kind: 'activity_update' | 'packing_items' | 'note' | 'ticket' | 'meal' | 'grocery' | 'entity_update' | 'entity_delete' | 'operation';
}>;

/** A stored value as a person reads it: dates without the clock, yes/no for flags. */
function shown(v: unknown, t: ReturnType<typeof useTranslation>['t']): string {
  if (v === null || v === undefined || v === '') return t('ai.cards.empty');
  if (typeof v === 'boolean') return v ? t('ai.cards.yes') : t('ai.cards.no');
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) return v.endsWith('T00:00:00.000Z') ? v.slice(0, 10) : v.slice(0, 16).replace('T', ' ');
  return String(v);
}

function CardShell({ icon, title, subtitle, saving, onAccept, onDiscard, children }: {
  icon: LucideIcon;
  title: string;
  subtitle?: string;
  saving: boolean;
  onAccept: () => void;
  onDiscard: () => void;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
      <div className="px-3 py-2.5 flex items-start gap-2.5">
        <div className="w-8 h-8 rounded-lg bg-teal-50 dark:bg-teal-900/40 flex items-center justify-center shrink-0">
          <Icon as={icon} size={15} className="text-teal-600 dark:text-teal-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-semibold text-slate-800 dark:text-slate-100 leading-snug">{title}</p>
          {subtitle && <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{subtitle}</p>}
          {children && <div className="mt-1.5 space-y-0.5 text-[12px] text-slate-600 dark:text-slate-300">{children}</div>}
        </div>
      </div>
      <div className="flex items-center gap-2 px-3 py-2 border-t border-slate-100 dark:border-slate-800">
        <button
          onClick={onAccept}
          disabled={saving}
          className="flex-1 h-8 rounded-lg bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white text-[12px] font-semibold flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
        >
          {saving ? <Spinner size="sm" /> : <Icon as={Check} size={13} />}
          {saving ? t('ai.saving') : t('ai.accept')}
        </button>
        <button
          onClick={onDiscard}
          disabled={saving}
          className="h-8 px-3 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 text-[12px] font-medium transition-colors cursor-pointer disabled:opacity-50"
        >
          {t('ai.discard')}
        </button>
      </div>
    </div>
  );
}

const OPERATION_ICON: Record<AiOperation, LucideIcon> = {
  trip_dates: Calendar, settle_debt: Wallet, outfit_add: Shirt, ingredient_add: Utensils, meal_stop: MapPin, activities_duplicate: Copy,
};

const day = (iso?: string) => (iso ? iso.slice(0, 10) : '');
const time = (iso?: string) => (iso && iso.length >= 16 ? iso.slice(11, 16) : '');

function Body({ pending }: { pending: TripPending }) {
  const { t } = useTranslation();
  const empty = t('ai.cards.empty');

  switch (pending.kind) {
    case 'activity_update': {
      const { changes, before } = pending.data;
      return (
        <>
          {(Object.keys(changes) as (keyof typeof changes)[]).map(field => (
            <p key={field}>
              <span className="text-slate-400">{t(`ai.cards.field.${field}`)}: </span>
              <span className="line-through text-slate-400">{String(before[field] ?? empty)}</span>
              {' → '}
              <span className="font-medium">{String(changes[field] ?? empty)}</span>
            </p>
          ))}
        </>
      );
    }
    case 'packing_items':
      return (
        <>
          {pending.data.add.map(a => <p key={`a-${a.sectionId}-${a.text}`}>+ {a.text} <span className="text-slate-400">· {a.sectionTitle}</span></p>)}
          {pending.data.check.map(c => (
            <p key={`c-${c.itemId}`}>{c.checked ? t('ai.cards.packed', { item: c.text }) : t('ai.cards.unpacked', { item: c.text })}</p>
          ))}
        </>
      );
    case 'note':
      return <p className="whitespace-pre-line line-clamp-6">{pending.data.content}</p>;
    case 'ticket': {
      const ticket = pending.data;
      if (ticket.kind === 'flight') {
        return (
          <>
            {ticket.data.legs.map((l, i) => (
              <p key={i}>
                {l.fromAirport} → {l.toAirport} · {day(l.departureTime)} {time(l.departureTime)}
                {l.flightNumber ? ` · ${l.flightNumber}` : ''}
              </p>
            ))}
          </>
        );
      }
      if (ticket.kind === 'hotel') {
        return <p>{day(ticket.data.checkIn)} → {day(ticket.data.checkOut)} · {t('ai.cards.nights', { count: ticket.data.nights })}</p>;
      }
      const b = ticket.data;
      return <p>{day(b.date)}{b.startTime ? ` · ${b.startTime}${b.endTime ? `–${b.endTime}` : ''}` : ''}{b.venue ? ` · ${b.venue}` : ''}</p>;
    }
    case 'meal':
      return pending.data.venueName
        ? <p>{t('ai.cards.at', { place: pending.data.venueName })}</p>
        : <>{pending.data.ingredients.map(i => <p key={i.name}>{i.name} <span className="text-slate-400">· {i.amount} {i.unit}</span></p>)}</>;
    case 'grocery':
      return <>{pending.data.items.map(i => <p key={i.text}>{i.text} <span className="text-slate-400">· {i.amount} {i.unit}</span></p>)}</>;
    case 'entity_update': {
      const { changes, before } = pending.data;
      return (
        <>
          {Object.keys(changes).map(field => (
            <p key={field}>
              <span className="text-slate-400">{t(`ai.cards.field.${field}`, { defaultValue: field })}: </span>
              <span className="line-through text-slate-400">{shown(before[field], t)}</span>
              {' → '}
              <span className="font-medium">{shown(changes[field], t)}</span>
            </p>
          ))}
        </>
      );
    }
    case 'entity_delete':
      return <>{pending.data.items.map(i => <p key={i.id}>– {i.label}</p>)}</>;
    case 'operation':
      return <>{pending.data.lines.map((l, i) => <p key={i}>{t(l.key, l.params)}</p>)}</>;
  }
}

function header(pending: TripPending, t: ReturnType<typeof useTranslation>['t']): { icon: LucideIcon; title: string; subtitle?: string } {
  switch (pending.kind) {
    case 'activity_update': return { icon: Pencil, title: t('ai.cards.editStop', { title: pending.data.title }) };
    case 'packing_items':   return { icon: Luggage, title: t('ai.cards.packingItems') };
    case 'note':            return { icon: FileText, title: pending.data.title || t('ai.cards.note'), subtitle: t('ai.cards.note') };
    case 'meal':
      return {
        icon: Utensils,
        title: pending.data.title,
        subtitle: `${t('ai.cards.day', { n: pending.data.dayNumber })} · ${t(`meals.slot.${pending.data.slot}`)}`,
      };
    case 'grocery':         return { icon: ShoppingCart, title: t('ai.cards.grocery') };
    case 'entity_update':
      return { icon: Pencil, title: t('ai.cards.editItem', { label: pending.data.label }), subtitle: t(`ai.cards.entity.${pending.data.entity}_one`) };
    case 'operation':
      return { icon: OPERATION_ICON[pending.data.op], title: t(pending.data.title.key, pending.data.title.params) };
    case 'entity_delete':
      return {
        icon: Trash2,
        title: t('ai.cards.deleteItems', { count: pending.data.items.length }),
        subtitle: t(`ai.cards.entity.${pending.data.entity}_other`),
      };
    case 'ticket': {
      const ticket = pending.data;
      if (ticket.kind === 'flight') return { icon: Plane, title: t('ai.cards.flight'), subtitle: ticket.data.legs[0]?.airline };
      if (ticket.kind === 'hotel') return { icon: BedDouble, title: ticket.data.name, subtitle: t('ai.cards.stay') };
      return { icon: Ticket, title: ticket.data.title, subtitle: t('ai.cards.booking') };
    }
  }
}

export function TripProposalCard({ pending, onAccept, onDiscard }: {
  pending: TripPending;
  onAccept: () => void;
  onDiscard: () => void;
}) {
  const { t } = useTranslation();
  const { icon, title, subtitle } = header(pending, t);
  return (
    <CardShell icon={icon} title={title} subtitle={subtitle} saving={pending.status === 'saving'} onAccept={onAccept} onDiscard={onDiscard}>
      <Body pending={pending} />
    </CardShell>
  );
}
